import { useCallback, useEffect, useState } from 'react';
import {
  BookOpen,
  FileText,
  Loader2,
  MessageSquare,
  Pencil,
  Pin,
  PinOff,
  Search,
  X,
} from 'lucide-react';
import {
  fetchChatList,
  fetchChatMessages,
  updateChatMeta,
  type ChatThreadSummary,
} from '../api/chatHistory';
import { Button } from '../components/atoms/Button';
import { InlineTitleEditor } from '../components/atoms/InlineTitleEditor';
import { cn } from '../lib/utils';
import type { ChatMessage } from './types';

interface Props {
  open: boolean;
  onClose: () => void;
  onSelectThread: (args: {
    thread: ChatThreadSummary;
    messages: ChatMessage[];
  }) => void;
}

type Tab = 'research' | 'draft';

/**
 * History drawer.
 *
 * Design mirrors the web app's sidebar list (frontend-prod UtilCard):
 *   - Icon + title card per thread
 *   - Grouped by date bucket (Today / Yesterday / Previous 7 days / Older)
 *   - Relative timestamps, not absolute noise
 *   - Subtle hover + loading affordances, no heavy dividers
 *
 * Tabs stay the same: Research + Drafts, both unified across web and plugin.
 */
export function ChatHistorySidebar({ open, onClose, onSelectThread }: Props): JSX.Element | null {
  const [tab, setTab] = useState<Tab>('research');
  const [threads, setThreads] = useState<ChatThreadSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingThreadId, setLoadingThreadId] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchChatList(tab)
      .then((list) => {
        if (!cancelled) setThreads(list);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message ?? 'Failed to load history');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, tab]);

  // Reset the search box when the drawer opens or the tab changes so the
  // user never sees a stale filter after switching contexts.
  useEffect(() => {
    setQuery('');
  }, [open, tab]);

  const handleSelect = async (thread: ChatThreadSummary) => {
    setLoadingThreadId(thread.chatId);
    try {
      const messages = await fetchChatMessages(thread.chatId);
      onSelectThread({ thread, messages });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load chat');
    } finally {
      setLoadingThreadId(null);
    }
  };

  // F6 — optimistic pin/unpin. Rolls back on network failure.
  const handleTogglePin = useCallback(
    async (chatId: string, nextPinned: boolean) => {
      setThreads((prev) =>
        prev.map((t) => (t.chatId === chatId ? { ...t, isPinned: nextPinned } : t)),
      );
      try {
        await updateChatMeta({ chatId, isPinned: nextPinned });
      } catch (err) {
        // Roll back UI on error.
        setThreads((prev) =>
          prev.map((t) => (t.chatId === chatId ? { ...t, isPinned: !nextPinned } : t)),
        );
        setError(err instanceof Error ? err.message : 'Could not update pin state');
      }
    },
    [],
  );

  // F7 — optimistic rename. Rolls back on network failure.
  const handleRename = useCallback(
    async (chatId: string, nextName: string) => {
      const prevName = threads.find((t) => t.chatId === chatId)?.name ?? '';
      setThreads((prev) =>
        prev.map((t) => (t.chatId === chatId ? { ...t, name: nextName } : t)),
      );
      try {
        await updateChatMeta({ chatId, title: nextName, isUserRenamed: true });
      } catch (err) {
        setThreads((prev) =>
          prev.map((t) => (t.chatId === chatId ? { ...t, name: prevName } : t)),
        );
        setError(err instanceof Error ? err.message : 'Could not rename');
      }
    },
    [threads],
  );

  if (!open) return null;

  const filtered = query.trim()
    ? threads.filter((t) =>
        (t.name || 'Untitled').toLowerCase().includes(query.trim().toLowerCase()),
      )
    : threads;
  // F6 — partition into pinned + unpinned; render Pinned section above the
  // date-bucketed rest (sorted by updatedAt DESC).
  const pinned = filtered
    .filter((t) => t.isPinned)
    .sort(
      (a, b) =>
        new Date(b.updatedAt ?? b.createdAt).getTime() -
        new Date(a.updatedAt ?? a.createdAt).getTime(),
    );
  const unpinned = filtered.filter((t) => !t.isPinned);
  const grouped = groupByDate(unpinned);

  // z-30 keeps the sidebar above Framer-Motion-transformed elements like
  // the ThinkingPill's PulsingLogo. Without this, GPU-composited animation
  // children at z-10 bleed through into the History page even though their
  // parent ScrollArea is hidden underneath.
  return (
    <div className="absolute inset-0 bg-background z-30 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <h2 className="text-sm font-semibold">History</h2>
        <Button variant="ghost" size="icon" onClick={onClose} title="Close">
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border text-xs">
        <TabButton active={tab === 'research'} onClick={() => setTab('research')}>
          <MessageSquare className="w-3.5 h-3.5" />
          Research
        </TabButton>
        <TabButton active={tab === 'draft'} onClick={() => setTab('draft')}>
          <FileText className="w-3.5 h-3.5" />
          Drafts
        </TabButton>
      </div>

      {/* Search */}
      <div className="px-3 py-2 border-b border-border">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${tab === 'research' ? 'research' : 'drafts'}`}
            className="w-full pl-7 pr-2 py-1.5 text-xs bg-muted/50 border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-primary/40 placeholder:text-muted-foreground"
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto scrollbar-thin py-2">
        {loading && (
          <div className="flex items-center justify-center py-10 text-xs text-muted-foreground">
            <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
            Loading…
          </div>
        )}
        {error && (
          <div className="mx-3 my-2 px-3 py-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md">
            {error}
          </div>
        )}
        {!loading && !error && filtered.length === 0 && (
          <EmptyHistory tab={tab} query={query} />
        )}
        {!loading && !error && pinned.length > 0 && (
          <div className="mb-2">
            <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground font-medium flex items-center gap-1">
              <Pin className="w-3 h-3" />
              Pinned
            </div>
            <div className="px-2">
              {pinned.map((t) => (
                <ThreadRow
                  key={t.chatId}
                  thread={t}
                  tab={tab}
                  loading={loadingThreadId === t.chatId}
                  disabled={loadingThreadId !== null}
                  onSelect={() => handleSelect(t)}
                  onTogglePin={(next) => handleTogglePin(t.chatId, next)}
                  onRename={(next) => handleRename(t.chatId, next)}
                />
              ))}
            </div>
          </div>
        )}
        {!loading &&
          !error &&
          unpinned.length > 0 &&
          grouped.map((group) => (
            <div key={group.label} className="mb-2">
              <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                {group.label}
              </div>
              <div className="px-2">
                {group.items.map((t) => (
                  <ThreadRow
                    key={t.chatId}
                    thread={t}
                    tab={tab}
                    loading={loadingThreadId === t.chatId}
                    disabled={loadingThreadId !== null}
                    onSelect={() => handleSelect(t)}
                    onTogglePin={(next) => handleTogglePin(t.chatId, next)}
                    onRename={(next) => handleRename(t.chatId, next)}
                  />
                ))}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

function ThreadRow({
  thread,
  tab,
  loading,
  disabled,
  onSelect,
  onTogglePin,
  onRename,
}: {
  thread: ChatThreadSummary;
  tab: Tab;
  loading: boolean;
  disabled: boolean;
  onSelect: () => void;
  /** F6 */
  onTogglePin?: (next: boolean) => void;
  /** F7 */
  onRename?: (next: string) => void;
}): JSX.Element {
  const title = thread.name?.trim() || 'Untitled';
  const when = thread.updatedAt ?? thread.createdAt;
  const Icon = tab === 'research' ? MessageSquare : FileText;
  const [editing, setEditing] = useState(false);

  // Stop click propagation so action buttons don't open the chat.
  const stop = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div
      role="button"
      tabIndex={editing ? -1 : 0}
      onClick={editing ? undefined : onSelect}
      onKeyDown={(e) => {
        if (editing) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      aria-disabled={disabled}
      className={cn(
        'group w-full text-left flex items-center gap-2 px-2.5 py-2 rounded-md transition-colors',
        editing
          ? 'bg-accent cursor-text'
          : 'hover:bg-accent cursor-pointer',
        disabled && !editing && 'opacity-60 cursor-not-allowed',
        loading && 'bg-accent',
      )}
      title={editing ? undefined : title}
    >
      <div className="w-6 h-6 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
        {loading ? (
          <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
        ) : (
          <Icon className="w-3.5 h-3.5 text-muted-foreground" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        {editing && onRename ? (
          <InlineTitleEditor
            initial={title === 'Untitled' ? '' : title}
            compact
            onSave={(next) => {
              setEditing(false);
              if (next && next !== title) onRename(next);
            }}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <>
            <div className="text-xs font-medium text-foreground truncate leading-tight">
              {title}
            </div>
            <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">
              {formatRelativeTime(when)}
            </div>
          </>
        )}
      </div>

      {/* F7 — rename trigger. Hidden until row hover (or when already pinned
          so users can see the trigger without hovering on touch). */}
      {!editing && onRename && (
        <button
          type="button"
          onClick={(e) => {
            stop(e);
            setEditing(true);
          }}
          className={cn(
            'opacity-0 group-hover:opacity-100 transition-opacity',
            'w-6 h-6 rounded flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted',
          )}
          title="Rename"
          aria-label="Rename chat"
        >
          <Pencil className="w-3 h-3" />
        </button>
      )}

      {/* F6 — pin trigger. Always visible if pinned; hover-revealed otherwise. */}
      {!editing && onTogglePin && (
        <button
          type="button"
          onClick={(e) => {
            stop(e);
            onTogglePin(!thread.isPinned);
          }}
          className={cn(
            'w-6 h-6 rounded flex items-center justify-center transition-opacity',
            thread.isPinned
              ? 'opacity-100 text-primary hover:bg-muted'
              : 'opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground hover:bg-muted',
          )}
          title={thread.isPinned ? 'Unpin' : 'Pin'}
          aria-label={thread.isPinned ? 'Unpin chat' : 'Pin chat'}
        >
          {thread.isPinned ? (
            <Pin className="w-3 h-3 fill-current" />
          ) : (
            <PinOff className="w-3 h-3" />
          )}
        </button>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 flex-1 justify-center py-2 transition-colors',
        active
          ? 'text-foreground border-b-2 border-primary -mb-px font-medium'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function EmptyHistory({ tab, query }: { tab: Tab; query: string }): JSX.Element {
  if (query.trim()) {
    return (
      <div className="flex flex-col items-center justify-center text-center px-6 py-10 text-muted-foreground">
        <Search className="w-5 h-5 mb-2" />
        <p className="text-xs">No matches for &quot;{query.trim()}&quot;.</p>
      </div>
    );
  }
  const copy =
    tab === 'research'
      ? 'No research threads yet. Your first question will appear here.'
      : 'No drafts yet. Start editing a document or create one in Draft mode.';
  return (
    <div className="flex flex-col items-center justify-center text-center px-6 py-10 text-muted-foreground">
      <BookOpen className="w-5 h-5 mb-2" />
      <p className="text-xs">{copy}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Time formatting + date grouping
// ---------------------------------------------------------------------------

interface ThreadGroup {
  label: string;
  items: ChatThreadSummary[];
}

function groupByDate(threads: ChatThreadSummary[]): ThreadGroup[] {
  const now = new Date();
  const today = startOfDay(now);
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(today.getDate() - 30);

  const buckets: Record<string, ChatThreadSummary[]> = {
    Today: [],
    Yesterday: [],
    'Previous 7 days': [],
    'Previous 30 days': [],
    Older: [],
  };

  for (const t of threads) {
    const when = new Date(t.updatedAt ?? t.createdAt);
    if (Number.isNaN(when.getTime())) {
      buckets.Older.push(t);
      continue;
    }
    const dayStart = startOfDay(when);
    if (dayStart.getTime() === today.getTime()) buckets.Today.push(t);
    else if (dayStart.getTime() === yesterday.getTime()) buckets.Yesterday.push(t);
    else if (dayStart >= sevenDaysAgo) buckets['Previous 7 days'].push(t);
    else if (dayStart >= thirtyDaysAgo) buckets['Previous 30 days'].push(t);
    else buckets.Older.push(t);
  }

  const order = ['Today', 'Yesterday', 'Previous 7 days', 'Previous 30 days', 'Older'];
  return order
    .filter((label) => buckets[label].length > 0)
    .map((label) => ({ label, items: buckets[label] }));
}

function startOfDay(d: Date): Date {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function formatRelativeTime(input: string | number | Date): string {
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  const now = Date.now();
  const diffMs = now - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHr = Math.floor(diffMs / 3_600_000);
  const diffDay = Math.floor(diffMs / 86_400_000);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;

  const sameYear = d.getFullYear() === new Date().getFullYear();
  const opts: Intl.DateTimeFormatOptions = sameYear
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' };
  return d.toLocaleDateString(undefined, opts);
}
